'use client';

import { createContext, useContext } from 'react';

import type { AbstractIntlMessages } from 'next-intl';

/**
 * The locale's messages BEFORE terminology is applied: `$t(terms:…)` tokens
 * intact, `terms` at the stock defaults.
 *
 * The root provider's messages are resolved for whichever account the page
 * first loaded with, and a resolved message has lost its token: once
 * "$t(terms:fortnightly) Thresholds" becomes "Biweekly Thresholds", no later
 * merge can turn it into "Fortnightly Thresholds". Switching accounts with a
 * client-side navigation reuses that root layout, so `TerminologyProvider`
 * re-resolves from this source instead of from the inherited messages.
 */
const LocaleMessagesSourceContext = createContext<AbstractIntlMessages | null>(
  null,
);

export function LocaleMessagesSourceProvider({
  messages,
  children,
}: React.PropsWithChildren<{ messages: AbstractIntlMessages }>) {
  return (
    <LocaleMessagesSourceContext value={messages}>
      {children}
    </LocaleMessagesSourceContext>
  );
}

/** The unresolved locale messages, or `null` outside the root provider. */
export function useLocaleMessagesSource(): AbstractIntlMessages | null {
  return useContext(LocaleMessagesSourceContext);
}
